import { useCallback, useRef, useState } from 'react';
import { Paperclip, Smile, Code2, Send } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { cn } from '../../../components/ui/utils';

export interface TeamChatComposerProps {
  disabled?: boolean;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  onSend: () => void | Promise<void>;
  onFiles?: (files: File[]) => void | Promise<void>;
  onTyping?: () => void;
  compact?: boolean;
  /** Hint under toolbar */
  hint?: string;
}

export function TeamChatComposer({
  disabled,
  placeholder = 'Message…',
  value,
  onChange,
  onSend,
  onFiles,
  onTyping,
  compact,
  hint,
}: TeamChatComposerProps) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const insertCodeFence = useCallback(() => {
    const fence = '\n```\n\n```\n';
    onChange(value + fence);
    requestAnimationFrame(() => taRef.current?.focus());
  }, [onChange, value]);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length && onFiles) void onFiles(files);
    },
    [onFiles],
  );

  return (
    <div
      className={cn(
        'relative rounded-2xl border transition-all duration-300 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]',
        dragOver ? 'border-cyber-cyan/50 bg-cyber-cyan/10 shadow-md shadow-cyber-cyan/10' : 'border-border/50 bg-card-bg/50 shadow-sm',
        'backdrop-blur-md',
      )}
      onDragEnter={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
    >
      {dragOver ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-background/75 text-sm font-medium text-cyber-cyan backdrop-blur-[2px]">
          Drop files to attach
        </div>
      ) : null}
      <input ref={fileRef} type="file" className="hidden" multiple onChange={(e) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        e.target.value = '';
        if (files.length && onFiles) void onFiles(files);
      }} />
      <div className="flex items-center gap-0.5 border-b border-border/30 px-1.5 py-1">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-muted-foreground hover:text-cyan-400 transition-colors duration-200 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] active:scale-[0.96]"
          disabled={disabled}
          onClick={() => fileRef.current?.click()}
          title="Attach files"
        >
          <Paperclip className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-muted-foreground hover:text-amber-400 transition-colors duration-200 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] active:scale-[0.96]"
          disabled={disabled}
          title="Emoji (paste or type :)"
          onClick={() => {
            onChange(`${value}${value && !value.endsWith(' ') ? ' ' : ''}:wave:`);
            taRef.current?.focus();
          }}
        >
          <Smile className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-muted-foreground hover:text-violet-400 transition-colors duration-200 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] active:scale-[0.96]"
          disabled={disabled}
          title="Insert code block"
          onClick={insertCodeFence}
        >
          <Code2 className="h-4 w-4" />
        </Button>
        <span className="ml-auto text-[10px] text-muted-foreground/80 pr-2 hidden sm:inline">
          Markdown-friendly · paste images
        </span>
      </div>
      <div className={cn('flex gap-2 p-2', compact ? 'items-end' : 'items-end')}>
        <Textarea
          ref={taRef}
          rows={compact ? 2 : 3}
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          className={cn(
            'resize-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none max-h-40 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-border/40',
            compact ? 'min-h-[44px] text-sm' : 'min-h-[72px] text-sm leading-relaxed',
          )}
          onChange={(e) => {
            onChange(e.target.value);
            onTyping?.();
          }}
          onPaste={(e) => {
            const files = e.clipboardData?.files;
            if (files?.length && onFiles) {
              e.preventDefault();
              void onFiles(Array.from(files));
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void onSend();
            }
          }}
        />
        <Button
          type="button"
          size={compact ? 'icon' : 'default'}
          className={cn(
            'shrink-0 self-end rounded-xl transition-all duration-200 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]',
            compact ? 'h-10 w-10 shadow-md shadow-cyber-cyan/20 hover:shadow-lg hover:shadow-cyber-cyan/30 active:scale-[0.97]' : 'px-5 shadow-md shadow-cyber-cyan/15 hover:shadow-cyber-cyan/25',
          )}
          disabled={disabled}
          onClick={() => void onSend()}
        >
          {compact ? <Send className="h-4 w-4" /> : 'Send'}
        </Button>
      </div>
      {hint ? <p className="px-3 pb-2 text-[10px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
